import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchBankVerificationPolicy,
  fetchBankVerificationStatus,
  verifyBankAccount,
} from "../api/bankVerificationApi";
import { useNotify } from "../context/NotifyContext";

export default function useBankVerification({
  accountNumber = "",
  ifsc = "",
  name = "",
  bankId = "",
  bankName = "",
  sourceType = "SETTLEMENT",
  sourceId = "",
  onVerified,
} = {}) {
  const { notify } = useNotify();
  const requestLock = useRef(false);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  const [policy, setPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const cleanAcc = useMemo(
    () => String(accountNumber || "").replace(/\s/g, ""),
    [accountNumber]
  );
  const cleanIfsc = useMemo(
    () => String(ifsc || "").trim().toUpperCase(),
    [ifsc]
  );
  const cleanName = useMemo(
    () => String(name || "").trim(),
    [name]
  );

  const isValidAccount = /^\d{9,18}$/.test(cleanAcc);
  const isValidIfsc = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(cleanIfsc);
  const isValidInput = isValidAccount && isValidIfsc;

  const verificationRequired = policy?.verificationRequired !== false;

  // Load verification policy on mount / sourceType change
  useEffect(() => {
    let active = true;
    setPolicyLoading(true);
    fetchBankVerificationPolicy(sourceType).then((res) => {
      if (!active) return;
      setPolicy(
        res.success
          ? res.data
          : { verificationRequired: true, providerEnabled: false, error: res.message }
      );
      setPolicyLoading(false);
    });
    return () => {
      active = false;
    };
  }, [sourceType]);

  // Debounced DB status check (No Wingpay API hit)
  useEffect(() => {
    if (!isValidInput) {
      setLastResult(null);
      setStatusLoading(false);
      return undefined;
    }

    let active = true;
    setStatusLoading(true);

    const timer = setTimeout(async () => {
      try {
        const response = await fetchBankVerificationStatus({
          accountNumber: cleanAcc,
          ifsc: cleanIfsc,
          bankId,
          sourceType,
          ...(sourceId ? { sourceId } : {}),
        });

        if (!active) return;

        const data = response.data || {};
        const resultPayload = {
          ...data,
          success: response.success,
          message: response.message,
          accountNumber: cleanAcc,
          ifsc: cleanIfsc,
        };

        setLastResult(resultPayload);

        if (response.success && data.verified) {
          onVerifiedRef.current?.(resultPayload);
        }
      } catch (err) {
        if (!active) return;
        setLastResult(null);
      } finally {
        if (active) setStatusLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [cleanAcc, cleanIfsc, bankId, sourceType, sourceId, isValidInput]);

  const alreadyVerified = Boolean(
    lastResult?.verified === true ||
    lastResult?.status === "VERIFIED" ||
    lastResult?.verificationStatus === "VERIFIED"
  );
  const pending = lastResult?.status === "PENDING" || lastResult?.verificationStatus === "PENDING";
  const failed = lastResult?.status === "FAILED" || lastResult?.verificationStatus === "FAILED";
  const bypassed = Boolean(lastResult?.verificationBypassed);
  const ready = Boolean(!verificationRequired || alreadyVerified || bypassed);
  const beneficiaryName = lastResult?.beneficiaryName || "";

  // Perform Verification (Wingpay hit)
  const verify = useCallback(
    async ({ forceReverify = false, nameOverride = "" } = {}) => {
      if (requestLock.current || loading) return;

      if (!isValidAccount) {
        notify("Account number must be 9-18 digits", "warning");
        return;
      }
      if (!isValidIfsc) {
        notify("Valid 11-character IFSC code is required", "warning");
        return;
      }

      // if (!verificationRequired) {
      //   notify("Bank verification is not required", "info");
      //   return;
      // }

      // If already verified and user didn't ask for Dobara Verify, skip API call
      if (alreadyVerified && !forceReverify) {
        notify("Bank account is already verified", "info");
        return lastResult;
      }

      requestLock.current = true;
      setLoading(true);

      try {
        const payload = {
          accountNumber: cleanAcc,
          ifsc: cleanIfsc,
          name: nameOverride || cleanName,
          bankId,
          bankName,
          sourceType,
          forceReverify,
          ...(sourceId ? { sourceId } : {}),
        };

        const response = await verifyBankAccount(payload);
        const data = response.data || {};
        const nextResult = {
          ...data,
          success: response.success,
          message: response.message,
          accountNumber: cleanAcc,
          ifsc: cleanIfsc,
        };

        setLastResult(nextResult);

        if (response.success && data.verified) {
          notify(
            data.beneficiaryName
              ? `Bank Verified: ${data.beneficiaryName}`
              : response.message || "Bank account verified successfully",
            "success"
          );
          onVerifiedRef.current?.(nextResult);
        } else {
          notify(
            data.failureReason || response.message || "Bank verification failed",
            "error"
          );
        }

        return nextResult;
      } catch (err) {
        const errorMsg =
          err?.response?.data?.message || err?.message || "Verification request failed";
        notify(errorMsg, "error");
        return { success: false, message: errorMsg };
      } finally {
        requestLock.current = false;
        setLoading(false);
      }
    },
    [
      alreadyVerified,
      bankId,
      bankName,
      cleanAcc,
      cleanIfsc,
      cleanName,
      isValidAccount,
      isValidIfsc,
      lastResult,
      loading,
      notify,
      sourceId,
      sourceType,
      verificationRequired,
    ]
  );

  return {
    verify,
    loading,
    statusLoading,
    policyLoading,
    policy,
    verificationRequired,
    result: lastResult,
    ready,
    alreadyVerified,
    pending,
    failed,
    bypassed,
    beneficiaryName,
    isValidInput,
  };
}
